import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Check, MessageCircle, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import FlagshipCard from '../components/FlagshipCard';
import { useT } from '../i18n';
import { categoryPages } from '../data/categoryPages';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function CategoryPage({ slug }) {
  const { lang } = useT();
  const isEs = lang === 'es';
  const data = categoryPages[slug];
  const c = isEs ? data.es : data.en;
  const CatIcon = Icons[data.icon] || Icons.Sparkles;

  return (
    <div data-testid={`category-page-${slug}`}>
      <SEO title={`${c.title} ${c.titleAccent}`} description={c.intro} path={`/${slug}`} />

      {/* ------------------------------------------------ Hero */}
      <section className="relative bg-neutral-950 text-white overflow-hidden pt-28 md:pt-32">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.14] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 25%, #F77F11 1px, transparent 1.5px), radial-gradient(circle at 80% 75%, #F77F11 1px, transparent 1.5px)',
            backgroundSize: '130px 130px, 170px 170px',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F77F11]/10 blur-[120px] pointer-events-none"
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10 pt-10 pb-14 sm:py-20 lg:py-28">
          <motion.div {...fadeUp}>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#F77F11]/40 bg-[#F77F11]/10 px-4 py-1.5 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#ffb374]">
              <CatIcon className="w-3.5 h-3.5" />
              {c.eyebrow}
            </p>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-[-0.02em] max-w-3xl">
              {c.title}{' '}
              <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
                {c.titleAccent}
              </span>
            </h1>
            <p className="mt-6 text-neutral-300 text-base sm:text-lg leading-relaxed max-w-2xl">{c.intro}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                asChild
                className="bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-[0_12px_40px_-12px_rgba(247,127,17,0.7)]"
              >
                <Link to="/#audit" data-testid={`category-hero-audit-${slug}`} className="group/audit">
                  <span className="inline-grid text-center">
                    <span className="col-start-1 row-start-1 transition-all duration-200 group-hover/audit:opacity-0 group-hover/audit:-translate-y-1.5">
                      {isEs ? 'Auditoría Gratis' : 'Get My Free Audit'}
                    </span>
                    <span className="col-start-1 row-start-1 opacity-0 translate-y-1.5 transition-all duration-200 group-hover/audit:opacity-100 group-hover/audit:translate-y-0 font-bold tracking-wide">
                      {isEs ? '¡HAZLO YA!' : 'DO IT NOW'}
                    </span>
                  </span>
                  <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-200 group-hover/audit:translate-x-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-neutral-700 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-6 rounded-full text-[15px] font-semibold"
              >
                <Link to="/contact" data-testid={`category-hero-contact-${slug}`}>
                  <MessageCircle className="w-4 h-4 mr-1.5" />
                  {isEs ? 'Hablar con Rachel' : 'Talk to Rachel'}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------ "In plain words" */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-orange-50/70 to-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="kx-eyebrow mb-4">{isEs ? 'SIN TECNICISMOS' : 'NO JARGON'}</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
                {c.simpleHeading}
              </h2>
              <div className="mt-6 rounded-2xl bg-white border-l-4 border-[#F77F11] border border-orange-100 p-6 shadow-[0_20px_50px_-30px_rgba(247,127,17,0.4)]">
                <Sparkles className="w-5 h-5 text-[#F77F11] mb-3" />
                <p className="text-neutral-700 leading-relaxed text-[15.5px]">{c.simpleAnalogy}</p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-4">
                {c.simplePoints.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 rounded-xl bg-white border border-neutral-200 hover:border-[#F77F11]/60 transition-colors p-5"
                  >
                    <span className="w-7 h-7 shrink-0 rounded-full bg-orange-50 flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#F77F11]" />
                    </span>
                    <span className="text-[14.5px] text-neutral-700 leading-relaxed min-w-0">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------ Sub-services */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-12">
            <p className="kx-eyebrow mb-4">{c.eyebrow}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">{c.servicesHeading}</h2>
            <p className="mt-4 text-neutral-600">{c.servicesIntro}</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.services.map((s, i) => {
              const I = Icons[s.icon] || Icons.Sparkles;
              return (
                <motion.div key={s.href} {...fadeUp} transition={{ duration: 0.45, delay: i * 0.06 }}>
                  <Link
                    to={s.href}
                    className="group flex flex-col h-full rounded-2xl border border-neutral-200 hover:border-[#F77F11] bg-white p-7 transition-all hover:shadow-[0_30px_70px_-30px_rgba(247,127,17,0.45)] hover:-translate-y-1"
                    data-testid={`category-service-card-${s.href.replace(/\//g, '-').replace(/^-/, '')}`}
                  >
                    <div className="flex items-center justify-between mb-5">
                      <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center group-hover:bg-[#F77F11] transition-colors">
                        <I className="w-6 h-6 text-[#F77F11] group-hover:text-white transition-colors" />
                      </div>
                      {s.comingSoon && (
                        <span className="px-2.5 py-1 rounded-full bg-orange-50 text-[#F77F11] text-[10px] tracking-wide font-semibold uppercase">
                          {isEs ? 'Pronto' : 'Soon'}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-neutral-900">{s.name}</h3>
                    <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{s.what}</p>
                    <p className="mt-3 text-[13px] text-[#b45309] bg-orange-50/80 rounded-lg px-3 py-2 leading-relaxed italic">
                      {s.plain}
                    </p>
                    <span className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#F77F11]">
                      {isEs ? 'Ver servicio completo' : 'See full service'}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ "This is for you if…" */}
      <section className="py-16 lg:py-24 bg-neutral-950 text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-[#F77F11]/10 blur-[110px] pointer-events-none"
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="kx-eyebrow mb-4">{isEs ? '¿TE SUENA FAMILIAR?' : 'SOUND FAMILIAR?'}</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{c.forYouHeading}</h2>
          </motion.div>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {c.forYou.map((f, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                className="flex items-start gap-3 rounded-2xl bg-neutral-900/70 border border-neutral-800 hover:border-[#F77F11]/70 transition-colors p-6"
              >
                <span className="w-8 h-8 shrink-0 rounded-full bg-[#F77F11]/15 flex items-center justify-center text-[#F77F11] font-bold text-sm">
                  {i + 1}
                </span>
                <p className="text-[15px] text-neutral-300 leading-relaxed min-w-0">{f}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ Final CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-white to-orange-50/50">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <FlagshipCard context={slug} className="mb-14" />
        </div>
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <motion.div {...fadeUp}>
            <p className="kx-eyebrow mb-4">{isEs ? 'PASO 1 · GRATIS' : 'STEP 1 · FREE'}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight">
              {c.ctaTitle}
            </h2>
            <p className="mt-5 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
              {c.ctaText}
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Button
                asChild
                className="bg-[#F77F11] hover:bg-[#d8651a] text-white h-13 px-8 py-3.5 rounded-full text-base font-semibold shadow-[0_16px_50px_-16px_rgba(247,127,17,0.8)]"
              >
                <Link to="/#audit" data-testid={`category-cta-audit-${slug}`} className="group/audit">
                  <span className="inline-grid text-center">
                    <span className="col-start-1 row-start-1 transition-all duration-200 group-hover/audit:opacity-0 group-hover/audit:-translate-y-1.5">
                      {isEs ? 'Solicitar Auditoría Gratis' : 'Request My Free Audit'}
                    </span>
                    <span className="col-start-1 row-start-1 opacity-0 translate-y-1.5 transition-all duration-200 group-hover/audit:opacity-100 group-hover/audit:translate-y-0 font-bold tracking-wide">
                      {isEs ? '¡HAZLO YA!' : 'DO IT NOW'}
                    </span>
                  </span>
                  <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-200 group-hover/audit:translate-x-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-[#F77F11] text-[#F77F11] hover:bg-orange-50 hover:text-[#d8651a] h-13 px-7 py-3.5 rounded-full text-base font-semibold"
              >
                <Link to="/contact">{isEs ? 'Prefiero hablar con alguien' : 'I’d rather talk to someone'}</Link>
              </Button>
            </div>
            <p className="mt-5 text-[13px] text-neutral-500">
              {isEs
                ? 'Sin compromiso · Respuesta en menos de 24 horas · En español o inglés'
                : 'No commitment · Response within 24 hours · English or Spanish'}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
